/**
 * usePartnerActivity — Customer 360° Timeline hook (E-104)
 *
 * Kezeli a GET /partners/:id/activity hívást, lapozást (cursor), típus-filtert,
 * és az inline NOTE/CALL quick-add mutációt.
 */

import {
  createPartnerNote,
  getPartnerActivity,
  type ActivityItem,
  type ActivityType,
} from '@/api/partners';
import { useCallback, useEffect, useState } from 'react';

export interface UsePartnerActivityOptions {
  types?: ActivityType[] | undefined;
  limit?: number | undefined;
}

export interface UsePartnerActivityResult {
  items: ActivityItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  prependNote: (note: ActivityItem) => void;
}

export function usePartnerActivity(
  partnerId: string | undefined,
  opts: UsePartnerActivityOptions = {}
): UsePartnerActivityResult {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typesKey = opts.types ? opts.types.join(',') : '';
  const limit = opts.limit;

  const fetchFirst = useCallback(async () => {
    if (!partnerId) {
      setItems([]);
      setHasMore(false);
      setCursor(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const query = typesKey ? { types: typesKey.split(',') as ActivityType[], limit } : { limit };
      const res = await getPartnerActivity(partnerId, query);
      setItems(res.data.items);
      setCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load activity';
      setError(msg);
      console.error('usePartnerActivity:', err);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, typesKey, limit]);

  useEffect(() => {
    void fetchFirst();
  }, [fetchFirst]);

  const loadMore = useCallback(async () => {
    if (!partnerId || !cursor || !hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const query = typesKey
        ? { types: typesKey.split(',') as ActivityType[], limit, cursor }
        : { limit, cursor };
      const res = await getPartnerActivity(partnerId, query);
      setItems(prev => [...prev, ...res.data.items]);
      setCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load more activity';
      setError(msg);
      console.error('usePartnerActivity.loadMore:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [partnerId, cursor, hasMore, isLoadingMore, typesKey, limit]);

  const prependNote = useCallback((note: ActivityItem) => {
    setItems(prev => [note, ...prev]);
  }, []);

  return {
    items,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    refetch: fetchFirst,
    loadMore,
    prependNote,
  };
}

export interface UseCreatePartnerNoteResult {
  create: (input: {
    title: string;
    description?: string;
    type?: 'NOTE' | 'CALL';
    locationId?: string;
  }) => Promise<ActivityItem>;
  isSubmitting: boolean;
  error: string | null;
  reset: () => void;
}

export function useCreatePartnerNote(partnerId: string | undefined): UseCreatePartnerNoteResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (input: {
      title: string;
      description?: string;
      type?: 'NOTE' | 'CALL';
      locationId?: string;
    }): Promise<ActivityItem> => {
      if (!partnerId) throw new Error('partnerId is required');
      setIsSubmitting(true);
      setError(null);
      try {
        const res = await createPartnerNote(partnerId, input);
        return res.data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create note';
        setError(msg);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [partnerId]
  );

  const reset = useCallback(() => setError(null), []);

  return { create, isSubmitting, error, reset };
}
